import BarcodeScannerView from './BarcodeScannerView';
import CameraView from './CameraView';
import ScannerControls from './ScannerControls';
import { useMobileState } from '../provider/context';
import { FC, MutableRefObject, RefObject, useCallback, useEffect, useRef } from 'react';
import { ScannerInputFieldRef } from './ScannerInputField';
import { useCameraScanner } from '../provider/camera/CameraScannerContext';

type ScannerManagerProps = {
    isSendingRef?: MutableRefObject<boolean>;
    eslMacInputRef?: RefObject<ScannerInputFieldRef | null>;
    onBarcodeDetected: (barcode: string) => void;
    onModeChanged?: () => void;
};

const CaptureManager: FC<ScannerManagerProps> = ({
    isSendingRef,
    eslMacInputRef,
    onBarcodeDetected,
    onModeChanged,
}) => {
    const { state, dispatch } = useMobileState();
    const { settings } = state.storeState;
    const { mode, cameraId } = state.captureState;

    const { loading, loaded, cameras, refreshDevices } = useCameraScanner();

    const cameraStreamRef = useRef<MediaStream | null>(null);

    const scannerEnabled = mode === 'scanner';
    const cameraEnabled = mode === 'camera';

    const stopCameraStream = useCallback(() => {
        const stream = cameraStreamRef.current;
        if (!stream) return;

        stream.getTracks().forEach((track) => {
            track.stop();
        });

        cameraStreamRef.current = null;
    }, []);

    /**
     * Only load camera devices when user actually enters camera mode.
     * Do not touch camera hardware while scanner mode is active.
     */
    useEffect(() => {
        if (mode !== 'camera') return;

        void refreshDevices();
    }, [mode, refreshDevices]);

    /**
     * If user leaves camera mode, always release camera stream.
     */
    useEffect(() => {
        if (mode === 'camera') return;

        stopCameraStream();
    }, [mode, stopCameraStream]);

    /**
     * Safety cleanup when CaptureManager unmounts.
     */
    useEffect(() => {
        return () => {
            stopCameraStream();
        };
    }, [stopCameraStream]);

    const handleScannerToggle = useCallback(() => {
        if (isSendingRef?.current) return;

        eslMacInputRef?.current?.blur();
        stopCameraStream();

        dispatch({
            type: 'SET_CAPTURE_MODE',
            payload: 'scanner',
        });

        window.setTimeout(() => {
            onModeChanged?.();
        }, 0);
    }, [dispatch, eslMacInputRef, isSendingRef, onModeChanged, stopCameraStream]);

    const handleCameraToggle = useCallback(() => {
        if (isSendingRef?.current) return;

        eslMacInputRef?.current?.blur();

        dispatch({
            type: 'SET_CAPTURE_MODE',
            payload: 'camera',
        });

        window.setTimeout(() => {
            onModeChanged?.();
        }, 0);
    }, [dispatch, eslMacInputRef, isSendingRef, onModeChanged]);

    const handleSelectCamera = useCallback(
        (nextCameraId: string) => {
            if (nextCameraId === cameraId) return;

            stopCameraStream();

            dispatch({
                type: 'SET_CAMERA_ID',
                payload: nextCameraId,
            });
        },
        [cameraId, dispatch, stopCameraStream],
    );

    return (
        <>
            <ScannerControls
                scannerEnabled={scannerEnabled}
                onEnableScanner={handleScannerToggle}
                cameraEnabled={cameraEnabled}
                onEnableCamera={handleCameraToggle}
                cameras={cameras}
                selectedCamera={cameraId ?? ''}
                onSelectCamera={handleSelectCamera}
                cameraLoading={loading}
                cameraLoaded={loaded}
            />

            {mode === 'scanner' && <BarcodeScannerView scannerEnabled={scannerEnabled} />}

            {mode === 'camera' && (
                <CameraView
                    cameraEnabled={cameraEnabled}
                    onBarcodeDetected={onBarcodeDetected}
                    cameraStreamRef={cameraStreamRef}
                    autoConvertUpca={settings.auto_convert_upce_to_upca === '1'}
                    selectedDeviceId={cameraId ?? ''}
                    onCameraReady={() => {
                        void refreshDevices();
                    }}
                />
            )}
        </>
    );
};

export default CaptureManager;
